import {
  Checkbox,
  Divider,
  Group,
  LoadingOverlay,
  MultiSelect,
  NumberInput,
  Select,
  SimpleGrid,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { schemaResolver, useForm } from '@mantine/form';
import { useDebouncedState } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { IconHelp } from '@tabler/icons-react';
import { useSearchInvoicesInfinite } from 'api/index';
import { DebtorSelect } from 'components/DebtorSelect';
import { ModalFooter } from 'components/ModalFooter';
import dayjs from 'dayjs';
import { useProfile } from 'init/use-enforce-profile';
import { i18nKeys, useTranslation } from 'locales/index';
import { useEffect } from 'react';
import { useHistory } from 'react-router';
import { flatMap, isDefined, map, pipe, prop } from 'remeda';
import { useLoadLightUsers } from 'shared/hooks/use-load-light-users';
import { useLoadTask } from 'shared/hooks/use-load-task';
import { getNextPageParam } from 'shared/utils/query';
import { clientActions } from 'store/client/client.actions';
import { invoiceActions } from 'store/invoice/invoice.actions';

import { AdditionalAssigneesSelect } from '../AdditionalAssigneesSelect';
import { useLoadTaskTypes, useUpdateTask } from '../api';
import {
  type CreateTaskVariables,
  CreateTaskVariablesSchema,
  useCreateTask,
} from '../api/use-create-task';

const ACCOUNT_MANAGER_OPTION_VALUE = 'account_manager';

export type TaskFormProps = {
  refetchTasks?: VoidFunction;
  debtorId?: string;
  invoiceId?: string;
  batchData?: {
    filter_params?: Record<string, string>;
    view_id?: string;
    invoice_ids?: Array<string>;
    debtor_ids?: Array<string>;
  };
  taskId?: string;
};

export const TaskForm = ({
  refetchTasks,
  debtorId,
  invoiceId,
  batchData,
  taskId,
}: TaskFormProps) => {
  const { t } = useTranslation();
  const history = useHistory();

  const profile = useProfile();
  const { users = [] } = useLoadLightUsers();
  const { taskTypes } = useLoadTaskTypes();
  const { createTask } = useCreateTask();
  const { updateTask } = useUpdateTask();

  const isBatch = isDefined(batchData);

  const { task, isTaskLoading: isLoading } = useLoadTask(taskId);

  const form = useForm<CreateTaskVariables & { groupSameDebtor: boolean }>({
    initialValues: {
      userId: profile.id.toString(),
      additionalAssigneeIds: [],
      debtorId: debtorId ?? undefined,
      invoiceIds: invoiceId ? [invoiceId] : [],
      description: '',
      dueDate: undefined,
      startOffsetDays: undefined,
      taskTypeId: '3', // ID for "To-Do", the default task type
      title: '',
      groupSameDebtor: false,
    },
    validate: schemaResolver(CreateTaskVariablesSchema, { sync: true }),
  });

  useEffect(() => {
    if (isDefined(task)) {
      form.setValues({
        userId: task.assignee.id.toString() ?? profile.id.toString(),
        additionalAssigneeIds: task.additionalAssignees.map(a => a.id.toString()),
        debtorId: task.debtor?.id?.toString() ?? undefined,
        invoiceIds: task.invoices?.details?.map(e => e.id.toString()) ?? [],
        description: task.description ?? undefined,
        dueDate: task.dueDate?.toISOString() ?? undefined,
        startOffsetDays: task.startOffsetDays ?? undefined,
        taskTypeId: task.taskTypeId.toString(),
        title: task.title,
      });
    }
  }, [task]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-fill only on type change
  useEffect(() => {
    if (isDefined(taskId)) return;
    const selectedType = taskTypes.data.find(type => type.id.toString() === form.values.taskTypeId);
    if (!selectedType) return;
    if (!form.values.title && selectedType.defaultTitle) {
      form.setFieldValue('title', selectedType.defaultTitle);
    }
    if (!form.values.description && selectedType.defaultDescription) {
      form.setFieldValue('description', selectedType.defaultDescription);
    }
    if (form.values.dueDate === undefined && selectedType.defaultDueOffsetDays !== null) {
      form.setFieldValue(
        'dueDate',
        dayjs().add(selectedType.defaultDueOffsetDays, 'day').toISOString(),
      );
    }
  }, [form.values.taskTypeId, taskTypes.data, taskId]);

  const [invoiceSearchText, setInvoiceSearchText] = useDebouncedState('', 200);
  const { data: invoicesResponse } = useSearchInvoicesInfinite(
    {
      reference: invoiceSearchText,
      debtor_id: form.values.debtorId ?? undefined,
      page_limit: 100,
    },
    {
      query: {
        getNextPageParam,
      },
    },
  );

  const handleSubmit = () => {
    const additionalAssigneeIds = form.values.additionalAssigneeIds.filter(
      id => id !== form.values.userId,
    );

    if (isBatch) {
      const action = batchData.debtor_ids
        ? clientActions.batchAction
        : invoiceActions.actionsBatchAction;

      action({
        data: {
          ...batchData,
          action_type: 'task',
          user_id: form.values.userId,
          title: form.values.title,
          description: form.values.description,
          task_type_id: form.values.taskTypeId,
          due_date: form.values.dueDate,
          start_offset_days: form.values.startOffsetDays,
          group_same_debtor: form.values.groupSameDebtor,
          additional_assignee_ids: additionalAssigneeIds,
        },
        callback: () => {
          history.push(`/tasks`);
          modals.closeAll();
        },
      });
    } else if (taskId) {
      updateTask(
        {
          id: taskId.toString(),
          data: {
            debtorId: form.values.debtorId,
            invoiceIds: form.values.invoiceIds,
            description: form.values.description,
            dueDate: form.values.dueDate,
            startOffsetDays: form.values.startOffsetDays,
            taskTypeId: form.values.taskTypeId,
            title: form.values.title,
            ...assigneePayload(),
            additionalAssigneeIds,
          },
        },
        {
          onSuccess: () => {
            form.reset();
            modals.closeAll();
            refetchTasks?.();
          },
        },
      );
    } else {
      createTask(
        {
          debtorId: form.values.debtorId,
          invoiceIds: form.values.invoiceIds,
          description: form.values.description,
          dueDate: form.values.dueDate,
          startOffsetDays: form.values.startOffsetDays,
          taskTypeId: form.values.taskTypeId,
          title: form.values.title,
          ...assigneePayload(),
          additionalAssigneeIds,
        },
        {
          onSuccess: () => {
            form.reset();
            modals.closeAll();
            refetchTasks?.();
          },
        },
      );
    }
  };

  const assigneePayload = (): { assignToAccountManager: true } | { userId: string | undefined } =>
    form.values.userId === ACCOUNT_MANAGER_OPTION_VALUE
      ? { assignToAccountManager: true }
      : { userId: form.values.userId };

  const pages = invoicesResponse?.pages ?? [];
  const invoices = pipe(
    pages,
    flatMap(prop('data')),
    map(invoice => ({
      value: String(invoice.id),
      label: invoice.attributes.reference,
    })),
  );

  return (
    <>
      <LoadingOverlay visible={isLoading} zIndex={1000} overlayProps={{ radius: 'sm', blur: 2 }} />
      <SimpleGrid cols={2} p="md">
        <TextInput
          label={t(i18nKeys.TASK.ATTRIBUTES.TITLE)}
          placeholder={t(i18nKeys.TASK.ATTRIBUTES.TITLE_PLACEHOLDER)}
          required
          autoFocus
          {...form.getInputProps('title')}
        />
        <Select
          label={t(i18nKeys.TASK.ATTRIBUTES.TASK_TYPE)}
          placeholder={t(i18nKeys.TASK.ATTRIBUTES.TASK_TYPE_PLACEHOLDER)}
          data={taskTypes.data.map(type => ({
            label: type.name,
            value: type.id.toString(),
          }))}
          required
          {...form.getInputProps('taskTypeId')}
        />
        <Select
          label={t(i18nKeys.TASK.ATTRIBUTES.ASSIGNEE)}
          placeholder={t(i18nKeys.TASK.ATTRIBUTES.ASSIGNEE_PLACEHOLDER)}
          data={[
            {
              label: t(i18nKeys.TASK.ATTRIBUTES.ACCOUNT_MANAGER_OPTION),
              value: ACCOUNT_MANAGER_OPTION_VALUE,
            },
            ...users.map(user => ({
              label: user.name.full,
              value: user.id.toString(),
            })),
          ]}
          required
          searchable
          {...form.getInputProps('userId')}
        />
        <AdditionalAssigneesSelect
          users={users}
          primaryUserId={form.values.userId}
          {...form.getInputProps('additionalAssigneeIds')}
        />
        <DateInput
          label={t(i18nKeys.DUE_DATE)}
          placeholder={t(i18nKeys.TASK.ATTRIBUTES.DUE_DATE_PLACEHOLDER)}
          minDate={dayjs().toDate()}
          {...form.getInputProps('dueDate')}
        />
        <Group align="end" gap={6}>
          <NumberInput
            label={t(i18nKeys.TASK.ATTRIBUTES.START_OFFSET_DAYS)}
            placeholder="0"
            min={1}
            disabled={form.values.dueDate === undefined}
            allowNegative={false}
            allowDecimal={false}
            {...form.getInputProps('startOffsetDays')}
            flex={1}
          />
          <Group mb={6}>
            <Tooltip
              multiline
              w={250}
              withArrow
              transitionProps={{ duration: 200 }}
              label={t(i18nKeys.TASK.ATTRIBUTES.START_DATE_TOOLTIP)}
              position="top"
            >
              <IconHelp color="grey" size={24} stroke={1.5} />
            </Tooltip>
          </Group>
        </Group>
        {!isBatch && (
          <DebtorSelect
            value={form.values.debtorId ?? null}
            onChange={debtorId => form.setFieldValue('debtorId', debtorId ?? undefined)}
            error={form.errors.debtorId as string | undefined}
          />
        )}
        {!isBatch && (
          <MultiSelect
            label={t(i18nKeys.TASK.ATTRIBUTES.INVOICE)}
            placeholder={t(i18nKeys.TASK.ATTRIBUTES.INVOICE_PLACEHOLDER)}
            data={invoices}
            searchable
            onSearchChange={setInvoiceSearchText}
            defaultValue={[]}
            {...form.getInputProps('invoiceIds')}
          />
        )}
        <Textarea
          label={t(i18nKeys.DESCRIPTION)}
          placeholder={t(i18nKeys.TASK.ATTRIBUTES.DESCRIPTION_PLACEHOLDER)}
          style={{ gridColumn: '1 / -1' }}
          {...form.getInputProps('description')}
        />
        {isBatch && batchData.invoice_ids && (
          <Checkbox
            label={t(i18nKeys.FORM.TASK.GROUP_SAME_DEBTOR)}
            {...form.getInputProps('groupSameDebtor')}
          />
        )}
      </SimpleGrid>
      <Divider mb={0} mt="space4" />
      <ModalFooter
        onCancel={() => {
          modals.closeAll();
          form.reset();
        }}
        onSave={handleSubmit}
        isDisabled={!form.isValid()}
      />
    </>
  );
};
